import { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { store } from "../store";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useThemeColorMeta } from "../hooks/useThemeColorMeta";
import { i18nCodeFromApiLocale } from "../i18n/languages";
import { ensureLanguageLoaded } from "../../i18n";

const initialSettings = {
  direction: "ltr",
  language: "nb",
  pinSidebar: true,
  theme: "light",
};

const preferredTheme = () => {
  try {
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
};

// Utility function to detect user locale from various sources
const detectUserLocale = (user) => {
  if (!user) return null;

  // Check direct locale field first
  if (user.locale) {
    return user.locale;
  }

  // Check settings object
  if (user.settings?.locale) {
    return user.settings.locale;
  }

  // Check if there's a nested settings structure
  if (user.data?.settings?.locale) {
    return user.data.settings.locale;
  }
  return null;
};

// Utility function to map locale to i18n language code
// Rails `users.locale` (no/nb/nn, en, se/sv, pl) → i18n resource code; see SUPPORTED_LANGUAGES.
const mapLocaleToLanguage = (locale) => i18nCodeFromApiLocale(locale, "en");

export const restoreSettings = async () => {
  const user = await store.getState().session.currentUser;
  const defaults = {
    ...initialSettings,
    language: mapLocaleToLanguage(detectUserLocale(user)),
    theme: preferredTheme(),
  };

  try {
    const storedData = window.localStorage.getItem("settings");
    if (!storedData) return defaults;

    const stored = JSON.parse(storedData);
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) {
      return defaults;
    }
    return {
      ...defaults,
      ...stored,
      theme: stored.theme === "dark" || stored.theme === "light"
        ? stored.theme
        : defaults.theme,
    };
  } catch (err) {
    console.error("Error restoring settings:", err);
    return defaults;
  }
};

export const storeSettings = (settings) => {
  try {
    window.localStorage.setItem("settings", JSON.stringify(settings));
  } catch (err) {
    console.error("Could not save settings locally:", err);
  }
};

export const SettingsContext = createContext({
  settings: initialSettings,
  saveSettings: () => {},
});

export const SettingsProvider = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState(initialSettings);
  const [isInitialized, setIsInitialized] = useState(false);
  const { i18n } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  useThemeColorMeta(settings.theme);

  useEffect(() => {
    const initializeSettings = async () => {
      try {
        const restoredSettings = await restoreSettings();
        if (restoredSettings) {
          setSettings(restoredSettings);

          // Change language immediately after settings are restored
          if (restoredSettings.language) {
            await ensureLanguageLoaded(restoredSettings.language);
            await i18n.changeLanguage(restoredSettings.language);
          }
        }
      } catch (err) {
        console.error("Could not restore settings:", err);
        // Fallback to default settings
        setSettings(initialSettings);
        await ensureLanguageLoaded(initialSettings.language);
        await i18n.changeLanguage(initialSettings.language);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeSettings();
  }, [i18n]);

  // Re-initialize settings when user data becomes available
  useEffect(() => {
    if (currentUser && isInitialized) {
      const userLocale = detectUserLocale(currentUser);
      const mappedLanguage = mapLocaleToLanguage(userLocale);

      // Only update if the language is different from current
      if (mappedLanguage !== settings.language) {
        const updatedSettings = {
          ...settings,
          language: mappedLanguage,
        };

        setSettings(updatedSettings);
        storeSettings(updatedSettings);

        ensureLanguageLoaded(mappedLanguage).then(() => i18n.changeLanguage(mappedLanguage)).catch((err) => {
          console.error("Could not change language after user update:", err);
        });
      }
    }
  }, [currentUser, isInitialized, settings.language, i18n]);

  const saveSettings = async (updatedSettings) => {
    setSettings(updatedSettings);
    storeSettings(updatedSettings);

    if (
      updatedSettings.language &&
      updatedSettings.language !== settings.language
    ) {
      try {
        await ensureLanguageLoaded(updatedSettings.language);
        await i18n.changeLanguage(updatedSettings.language);
      } catch (err) {
        console.error("Could not change language:", err);
      }
    }
  };

  const priceFormatter = new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  if (!isInitialized) {
    return null;
  }

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings,
        priceFormatter,
        // Utility functions for debugging and locale management
        getCurrentLanguage: () => settings.language,
        getCurrentLocale: () => {
          const user = store.getState().session.currentUser;
          return detectUserLocale(user);
        },
        isLanguageLoaded: () => isInitialized,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const SettingsConsumer = SettingsContext.Consumer;

export const useSettings = () => useContext(SettingsContext);
